import { useQuery } from '@tanstack/react-query';
import { Duration } from 'luxon';
import { GroupStore } from '@app/api/GroupStore';
import apiResponse from '@stores/ApiResponse';

export enum RiskLevel {
  Clear = 0,
  Yellow = 1,
  Orange = 2,
  Red = 3,
}

export enum RiskCategory {
  Login = 'login',
  Survey = 'survey',
  Goal = 'goal',
  Pledge = 'pledge',
}

export interface RiskReason {
  level: RiskLevel;
  category: RiskCategory;
  reason: string;
}

export interface WeeklyData {
  week: number;
  goalCount: number;
  progressCount: number;
}

export interface RiskParticipant {
  cohortUserId: number;
  userId: number;
  name: string;
  email: string;
  phone: string;
  isActive: boolean;
  hasLoggedIn: boolean;
  hasCompletedGettingToKnowYouSurvey: boolean;
  hasActiveGoal: boolean;
  pledgeReceived: boolean;
  pledgeTransactionId: string;
  riskLevel: RiskLevel;
  riskReasons: RiskReason[];
  weeks: WeeklyData[];
}

export interface RiskCohort {
  cohortId: number;
  cohortName: string;
  currentWeekIndexZero: number;
  cohortDayNumber: number;
  startDate: string;
  isUpcoming: boolean;
  isClosed: boolean;
  participants: RiskParticipant[];
}

export interface RiskReportView {
  cohorts: RiskCohort[];
  generatedAt: string;
}

async function fetchRiskReport(): Promise<RiskReportView> {
  const store = new GroupStore();
  const result = await apiResponse.handleApiResponse<RiskReportView>(async () => await store.AdminGetRiskReport());
  return result as RiskReportView;
}

export const useRiskReport = (enabled: boolean = true) => {
  const query = useQuery({
    queryKey: ['riskReport'],
    queryFn: fetchRiskReport,
    staleTime: Duration.fromObject({ minutes: 2 }).milliseconds,
    enabled,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};
